import { Const as C } from './const';
import { Definition } from './definition';

const Const = C();

export namespace SharedEnv {
  export type Var = {
    // 旧ドメイン定義
    Domain: {
      base: { app: string };
      auth: { app: string; api: string; internalApi: string; interserviceApi: string };
      knowledge: { app: string; api: string };
      desk: { app: string; api: string };
      tabula: { app: string; api: string; internalApi: string };
      speak: { app: string; botApi: string; eventReceiverApi: string };
      debeso: { internalApi: string };
      intelligence: {
        app: string;
        api: string;
        agent: string;
      };
      coach: { app: string; api: string; worker: string };
      boilerplate: { app: string; api: string; internalApi: string };
      neuro: { app: string; api: string; internalApi: string; voiceAgentApi: string; agentTestApi: string };
      qantDataHub: { app: string; api: string; internalApi: string };
      // スマートFAQ。app = 管理画面 (front) / api = 管理 API / site = 公開サイト (Next.js SSR)。
      // site はテナントサブドメインのワイルドカード基底ドメイン (実際の配信は `<sub>.${site}`)。
      smartFaq: { app: string; api: string; site: string };
    };
    // Qantドメイン定義
    QantDomain: {
      base: { root: string; app: string };
      auth: { app: string; api: string; internalApi: string; interserviceApi: string };
      knowledge: { app: string; api: string; intentApi: string };
      desk: { app: string; api: string };
      debeso: { app: string; api: string; internalApi: string };
      troppus: { app: string; api: string; internalApi: string };
      tabula: { app: string; api: string; internalApi: string };
      lp: { introduction: string; lp: string };
      neuro: { app: string; api: string; internalApi: string; voiceAgentApi: string; agentTestApi: string };
      pubapi: { api: string };
    };
    PrivateDomain: {
      neuro: { internalApi: string };
      knowledge: { internalApi: string };
      voc: { internalApi: string };
      tabula: { internalApi: string };
      smartFaq: { internalApi: string };
    };
    // BaseのGCloudアカウントであることを変数名に反映させたい
    Gcloud: {
      ProjectId: string;
      ProjectNumber?: string;
      Pii?: { ProjectId: string; ProjectNumber: string };
      PlaidSystems?: { ProjectId: string; ProjectNumber: string };
    };
    ActiveGcloud: {
      ProjectId: string;
      ProjectNumber: string;
    };
    Github: { Env: string };
    Karte: { scriptUrl: string | undefined; issuesApiHost: string; vocApiHost: string; speakApiHost: string; adminDomain: string };
  };
}
export type Externals = {
  POSTGRES_USER_NAME: string;
  SHOW_DEV_TOOLS: boolean;
};
// Envによって動的に変化する定数などを定義する
// 各workspaceでclassをextendsし、シングルトンとして利用する
export class SharedEnv {
  static tfBackendBucketNameFor(env: Definition.Env, type: 'pii' | 'general' = 'general') {
    return `${env}-${type === 'pii' ? 'pii-' : ''}systems-tf-backend`;
  }

  readonly Name: Definition.Env;
  readonly IsDev: boolean;
  readonly IsTest: boolean;
  readonly IsEva: boolean;
  readonly IsProd: boolean;
  protected readonly BaseVar: SharedEnv.Var;
  readonly Var: SharedEnv.Var;
  // Terraformのbackendに指定するバケット名
  // TODO: 共有VPC対応したので、各PJ毎に設定を変更する必要がある
  tfBackendBucketName: string;
  // process.envから読み込まれる環境変数
  protected readonly BaseExternals: Externals;
  readonly Externals: Externals;

  constructor() {
    // 環境の指定が正しいことを確認
    const rawEnv = process.env.ENV ?? Definition.Env.Dev;
    const env = rawEnv.trim().toLowerCase();
    if (!(Object.values(Definition.Env) as string[]).includes(env)) {
      throw new Error(`Invalid ENV: ${env}`);
    }
    // アクティブなGCPアカウント設定が正しいことを確認
    const activeGcloudProject = (process.env.ACTIVE_GCLOUD_PROJECT as Definition.GcloudProjectName) || Definition.GcloudProjectName.Base;
    if (!(Object.values(Definition.GcloudProjectName) as string[]).includes(activeGcloudProject)) {
      throw new Error(`Invalid ACTIVE_GCP_PROJECT: ${activeGcloudProject}`);
    }
    this.Name = env as Definition.Env;
    this.IsDev = this.Name === Definition.Env.Dev;
    this.IsTest = this.Name === Definition.Env.Test;
    this.IsEva = this.Name === Definition.Env.Eva;
    this.IsProd = this.Name === Definition.Env.Prod;
    this.tfBackendBucketName = SharedEnv.tfBackendBucketNameFor(this.Name);
    this.BaseVar = {
      [Definition.Env.Dev]: {
        Domain: {
          base: { app: 'localhost:xxxx' },
          auth: {
            app: 'localhost:5173',
            api: 'localhost:3031',
            internalApi: 'localhost:3033',
            interserviceApi: 'localhost:3032',
          },
          knowledge: {
            app: 'localhost:5175',
            api: 'localhost:3000',
          },
          desk: {
            app: 'localhost:5176',
            api: 'localhost:3004',
          },
          tabula: {
            app: 'localhost:5179',
            api: 'localhost:3044',
            internalApi: 'localhost:3041',
          },
          speak: {
            app: 'localhost:5176',
            botApi: 'localhost:5177',
            eventReceiverApi: 'localhost:5178',
          },
          debeso: { internalApi: 'localhost:3062' },
          intelligence: {
            app: '',
            api: 'localhost:3053',
            agent: 'localhost:3054',
          },
          coach: {
            app: 'localhost:5182',
            api: 'localhost:3067',
            worker: 'localhost:3069',
          },
          boilerplate: {
            app: 'localhost:5177',
            api: 'localhost:3058',
            internalApi: 'localhost:3059',
          },
          neuro: {
            app: 'localhost:5180',
            api: 'localhost:3070',
            internalApi: 'localhost:3071',
            voiceAgentApi: 'localhost:3072',
            agentTestApi: 'localhost:3073',
          },
          qantDataHub: {
            app: 'localhost:xxxx',
            api: 'localhost:3065',
            internalApi: 'localhost:3066',
          },
          smartFaq: {
            app: 'localhost:5183',
            api: 'localhost:3090',
            site: 'localhost:3091',
          },
        },
        QantDomain: {
          base: { root: 'localhost:xxxx', app: 'localhost:xxxx' },
          auth: {
            app: 'localhost:5173',
            api: 'localhost:3031',
            internalApi: 'localhost:3033',
            interserviceApi: 'localhost:3032',
          },
          knowledge: {
            app: 'localhost:5175',
            api: 'localhost:3000',
            intentApi: 'localhost:3005',
          },
          desk: {
            app: 'localhost:5176',
            api: 'localhost:3004',
          },
          debeso: {
            app: 'localhost:5178',
            api: 'localhost:3063',
            internalApi: 'localhost:3062',
          },
          troppus: {
            app: 'localhost:5174',
            api: 'localhost:3035',
            internalApi: 'localhost:3036',
          },
          tabula: {
            app: 'localhost:5179',
            api: 'localhost:3044',
            internalApi: 'localhost:3041',
          },
          lp: { introduction: 'localhost:xxxx', lp: 'localhost:xxxx' },
          neuro: {
            app: 'localhost:5180',
            api: 'localhost:3070',
            internalApi: 'localhost:3071',
            voiceAgentApi: 'localhost:3072',
            agentTestApi: 'localhost:3073',
          },
          pubapi: {
            api: 'localhost:XXXX',
          },
        },
        PrivateDomain: {
          neuro: { internalApi: 'localhost:3071' },
          knowledge: {
            internalApi: 'localhost:3002',
          },
          voc: {
            internalApi: 'localhost:3334',
          },
          tabula: {
            internalApi: 'localhost:3041',
          },
          smartFaq: {
            internalApi: 'localhost:3092',
          },
        },
        Gcloud: Definition.Gcloud.dev,
        ActiveGcloud: {
          ProjectId: 'dummy',
          ProjectNumber: 'dummy',
        },
        Github: { Env: 'local' },
        Karte: {
          scriptUrl: undefined,
          issuesApiHost: 'localhost:3333',
          vocApiHost: 'localhost:3334',
          speakApiHost: 'localhost:3335',
          adminDomain: 'admin.karte.test',
        },
      } satisfies SharedEnv.Var,
      [Definition.Env.Test]: {
        Domain: {
          base: { app: 'localhost:test' },
          auth: {
            app: 'localhost:5173',
            api: 'localhost:3031',
            internalApi: 'localhost:3033',
            interserviceApi: 'localhost:3032',
          },
          knowledge: {
            app: 'localhost:5175',
            api: 'localhost:3000',
          },
          desk: {
            app: 'localhost:5176',
            api: 'localhost:3000',
          },
          tabula: {
            app: 'localhost:5179',
            api: 'localhost:3044',
            internalApi: 'localhost:3041',
          },
          speak: {
            app: 'localhost:5176',
            botApi: 'localhost:5177',
            eventReceiverApi: 'localhost:5178',
          },
          debeso: { internalApi: 'localhost:3062' },
          intelligence: {
            app: '',
            api: 'localhost:3053',
            agent: 'localhost:3054',
          },
          coach: {
            app: 'localhost:5182',
            api: 'localhost:3067',
            worker: 'localhost:3069',
          },
          boilerplate: {
            app: 'localhost:5177',
            api: 'localhost:3058',
            internalApi: 'localhost:3059',
          },
          neuro: {
            app: 'localhost:5180',
            api: 'localhost:3070',
            internalApi: 'localhost:3071',
            voiceAgentApi: 'localhost:3072',
            agentTestApi: 'localhost:3073',
          },
          qantDataHub: {
            app: 'localhost:xxxx',
            api: 'localhost:3065',
            internalApi: 'localhost:3066',
          },
          smartFaq: {
            app: 'localhost:5183',
            api: 'localhost:3090',
            site: 'localhost:3091',
          },
        },
        QantDomain: {
          base: { root: 'localhost:test', app: 'localhost:test' },
          auth: {
            app: 'localhost:5173',
            api: 'localhost:3031',
            internalApi: 'localhost:3033',
            interserviceApi: 'localhost:3032',
          },
          knowledge: {
            app: 'localhost:5175',
            api: 'localhost:3000',
            intentApi: 'localhost:3005',
          },
          desk: {
            app: 'localhost:5176',
            api: 'localhost:3004',
          },
          debeso: {
            app: 'localhost:5178',
            api: 'localhost:3062',
            internalApi: 'localhost:3062',
          },
          troppus: {
            app: 'localhost:5174',
            api: 'localhost:3035',
            internalApi: 'localhost:3036',
          },
          tabula: {
            app: 'localhost:5179',
            api: 'localhost:3044',
            internalApi: 'localhost:3041',
          },
          lp: { introduction: 'localhost:xxxx', lp: 'localhost:xxxx' },
          neuro: {
            app: 'localhost:5180',
            api: 'localhost:3070',
            internalApi: 'localhost:3071',
            voiceAgentApi: 'localhost:3072',
            agentTestApi: 'localhost:3073',
          },
          pubapi: {
            api: 'localhost: XXXX',
          },
        },
        PrivateDomain: {
          neuro: { internalApi: 'localhost:3071' },
          knowledge: {
            internalApi: 'localhost:3002',
          },
          voc: {
            internalApi: 'localhost:3334',
          },
          tabula: {
            internalApi: 'localhost:3041',
          },
          smartFaq: {
            internalApi: 'localhost:3092',
          },
        },
        Gcloud: { ProjectId: 'test-rt-systems' },
        ActiveGcloud: {
          ProjectId: 'dummy',
          ProjectNumber: 'dummy',
        },
        Github: { Env: 'test' },
        Karte: {
          scriptUrl: undefined,
          issuesApiHost: 'localhost:3333',
          vocApiHost: 'localhost:3334',
          speakApiHost: 'localhost:3335',
          adminDomain: 'admin.karte.test',
        },
      } satisfies SharedEnv.Var,
      [Definition.Env.Eva]: {
        Domain: {
          base: { app: `eva.${Const.Domain}` },
          auth: {
            app: `auth.eva.${Const.Domain}`,
            api: `api.auth.eva.${Const.Domain}`,
            internalApi: 'eva-auth-internal-api-282057777327.asia-northeast1.run.app',
            interserviceApi: `interservice-api.auth.eva.${Const.Domain}`,
          },
          knowledge: {
            app: `knowledge.eva.${Const.Domain}`,
            // TODO: qant.jpで修正する
            api: `api.knowledge.eva.${Const.Domain}`,
          },
          desk: {
            app: `desk.eva.${Const.Domain}`,
            api: `api.desk.eva.${Const.Domain}`,
          },
          tabula: {
            app: `tabula.eva.${Const.Domain}`,
            api: `api.tabula.eva.${Const.Domain}`,
            internalApi: `internal-api.tabula.eva.${Const.Domain}`,
          },
          speak: {
            app: `speak.eva.${Const.Domain}`,
            botApi: `bot.speak.eva.${Const.Domain}`,
            eventReceiverApi: `event-receiver.speak.eva.${Const.Domain}`,
          },
          debeso: {
            internalApi: 'eva-debeso-internal-api-ydbfakqh2a-an.a.run.app',
          },
          intelligence: {
            app: `intelligence.eva.${Const.Domain}`,
            api: '10.20.56.4',
            agent: `agent.intelligence.eva.${Const.Domain}`,
          },
          coach: {
            app: `coach.eva.${Const.Domain}`,
            api: 'eva-coach-api-977013186091.asia-northeast1.run.app',
            worker: 'eva-coach-worker-977013186091.asia-northeast1.run.app',
          },
          boilerplate: {
            app: `boilerplate.eva.${Const.Domain}`,
            api: `api.boilerplate.eva.${Const.Domain}`,
            internalApi: `internal-api.boilerplate.eva.${Const.Domain}`,
          },
          neuro: {
            app: `neuro.eva.${Const.Domain}`,
            api: `api.neuro.eva.${Const.Domain}`,
            internalApi: `internal-api.neuro.eva.${Const.Domain}`,
            voiceAgentApi: `voice-agent-api.neuro.eva.${Const.Domain}`,
            agentTestApi: `agent-test-api.neuro.eva.${Const.Domain}`,
          },
          qantDataHub: {
            app: `qant-data-hub.eva.${Const.Domain}`,
            api: `api.qant-data-hub.eva.${Const.Domain}`,
            internalApi: `internal-api.qant-data-hub.eva.${Const.Domain}`,
          },
          smartFaq: {
            app: `smart-faq.eva.${Const.Domain}`,
            api: `api.smart-faq.eva-${Const.QantDomain}`,
            // 公開サイトはテナントごとに `<sub>.eva-${Const.QantSupportDomain}` で配信するため、
            // 単一ドメインでは表現できない。ここにはワイルドカードの基底ドメインを入れる。
            site: `eva-${Const.QantSupportDomain}`,
          },
        },
        QantDomain: {
          base: {
            root: `eva-${Const.QantDomain}`,
            app: `app.eva-${Const.QantDomain}`,
          },
          auth: {
            app: `auth.eva-${Const.QantDomain}`,
            api: `api.auth.eva-${Const.QantDomain}`,
            internalApi: 'eva-auth-internal-api-282057777327.asia-northeast1.run.app',
            interserviceApi: `interservice-api.auth.eva-${Const.QantDomain}`,
          },
          knowledge: {
            app: `knowledge.eva-${Const.QantDomain}`,
            api: `api.knowledge.eva-${Const.QantDomain}`,
            intentApi: `intent-api.knowledge.eva-${Const.QantDomain}`,
          },
          desk: {
            app: `desk.eva-${Const.QantDomain}`,
            api: `api.desk.eva-${Const.QantDomain}`,
          },
          debeso: {
            app: `app.eva-${Const.QantDomain}`,
            api: `api.debeso.eva-${Const.QantDomain}`,
            internalApi: 'eva-debeso-internal-api-ydbfakqh2a-an.a.run.app',
          },
          troppus: {
            app: `troppus.eva-${Const.QantDomain}`,
            api: `api.troppus.eva-${Const.QantDomain}`,
            internalApi: 'eva-troppus-internal-api-941568787903.asia-northeast1.run.app',
          },
          tabula: {
            app: `tabula.eva-${Const.QantDomain}`,
            api: `api.tabula.eva-${Const.QantDomain}`,
            internalApi: `internal-api.tabula.eva-${Const.QantDomain}`,
          },
          lp: { introduction: `intro.eva-${Const.QantDomain}`, lp: `lp.eva-${Const.QantDomain}` },
          neuro: {
            app: `neuro.eva-${Const.QantDomain}`,
            api: `api.neuro.eva-${Const.QantDomain}`,
            internalApi: `internal-api.neuro.eva-${Const.QantDomain}`,
            voiceAgentApi: `voice-agent-api.neuro.eva-${Const.QantDomain}`,
            agentTestApi: `agent-test-api.neuro.eva-${Const.QantDomain}`,
          },
          pubapi: {
            api: `api.eva-${Const.QantDomain}`,
          },
        },
        PrivateDomain: {
          neuro: { internalApi: '10.20.80.7' },
          knowledge: {
            internalApi: `internal-api.knowledge.${Const.PrivateDomain}`,
          },
          voc: {
            internalApi: `internal-api.voc.${Const.PrivateDomain}`,
          },
          tabula: {
            internalApi: '10.20.64.3',
          },
          smartFaq: {
            internalApi: `internal-api.smart-faq.${Const.PrivateDomain}`,
          },
        },
        Gcloud: Definition.Gcloud.eva,
        ActiveGcloud: Definition.Gcloud.eva.GcpProject[activeGcloudProject],
        Github: { Env: 'evaluation' },
        Karte: {
          scriptUrl: 'https://evaluation-cdn-edge.dev-karte.com/8685b9bbee9d66808c9f1b6819323614/edge.js',
          issuesApiHost: '10.32.8.7',
          // Karte(karte-io-systems) 側 VoC internal LB。systems 内 voc app への切替は
          // PrivateDomain.voc.internalApi を使い、VoC 移行完了時に行う。
          vocApiHost: '10.32.8.7',
          speakApiHost: '10.32.8.7',
          adminDomain: 'evaluation.dev-karte.com',
        },
      } satisfies SharedEnv.Var,
      [Definition.Env.Prod]: {
        Domain: {
          base: { app: Const.Domain },
          auth: {
            app: `auth.${Const.Domain}`,
            api: `api.auth.${Const.Domain}`,
            internalApi: 'prod-auth-internal-api-402791595004.asia-northeast1.run.app',
            interserviceApi: `interservice-api.auth.${Const.Domain}`,
          },
          knowledge: {
            app: `knowledge.${Const.Domain}`,
            api: `api.knowledge.${Const.Domain}`,
          },
          desk: {
            app: `desk.${Const.Domain}`,
            api: `api.desk.${Const.Domain}`,
          },
          tabula: {
            app: `tabula.${Const.Domain}`,
            api: `api.tabula.${Const.Domain}`,
            internalApi: `internal-api.tabula.${Const.Domain}`,
          },
          speak: {
            app: `speak.${Const.Domain}`,
            botApi: `bot.speak.${Const.Domain}`,
            eventReceiverApi: `event-receiver.speak.${Const.Domain}`,
          },
          debeso: {
            internalApi: 'prod-debeso-internal-api-2sgwqw5hea-an.a.run.app',
          },
          intelligence: {
            app: `intelligence.${Const.Domain}`,
            api: '10.20.56.3',
            agent: `agent.intelligence.${Const.Domain}`,
          },
          coach: {
            app: `coach.${Const.Domain}`,
            api: 'prod-coach-api-335651521692.asia-northeast1.run.app',
            worker: 'prod-coach-worker-335651521692.asia-northeast1.run.app',
          },
          boilerplate: {
            app: `boilerplate.${Const.Domain}`,
            api: `api.boilerplate.${Const.Domain}`,
            internalApi: `internal-api.boilerplate.${Const.Domain}`,
          },
          neuro: {
            app: `neuro.${Const.Domain}`,
            api: `api.neuro.${Const.Domain}`,
            internalApi: `internal-api.neuro.${Const.Domain}`,
            voiceAgentApi: `voice-agent-api.neuro.${Const.Domain}`,
            agentTestApi: `agent-test-api.neuro.${Const.Domain}`,
          },
          qantDataHub: {
            app: `qant-data-hub.${Const.Domain}`,
            api: `api.qant-data-hub.${Const.Domain}`,
            internalApi: `internal-api.qant-data-hub.${Const.Domain}`,
          },
          smartFaq: {
            app: `smart-faq.${Const.Domain}`,
            api: `api.smart-faq.${Const.QantDomain}`,
            // 公開サイトはテナントごとに `<sub>.${Const.QantSupportDomain}` で配信するため、
            // 単一ドメインでは表現できない。ここにはワイルドカードの基底ドメインを入れる。
            site: Const.QantSupportDomain,
          },
        },
        QantDomain: {
          base: { root: Const.QantDomain, app: `app.${Const.QantDomain}` },
          auth: {
            app: `auth.${Const.QantDomain}`,
            api: `api.auth.${Const.QantDomain}`,
            internalApi: 'prod-auth-internal-api-402791595004.asia-northeast1.run.app',
            interserviceApi: `interservice-api.auth.${Const.QantDomain}`,
          },
          knowledge: {
            app: `knowledge.${Const.QantDomain}`,
            api: `api.knowledge.${Const.QantDomain}`,
            intentApi: `intent-api.knowledge.${Const.QantDomain}`,
          },
          desk: {
            app: `desk.${Const.QantDomain}`,
            api: `api.desk.${Const.QantDomain}`,
          },
          debeso: {
            app: `app.${Const.QantDomain}`,
            api: `api.debeso.${Const.QantDomain}`,
            internalApi: 'prod-debeso-internal-api-2sgwqw5hea-an.a.run.app',
          },
          troppus: {
            app: `troppus.${Const.QantDomain}`,
            api: `api.troppus.${Const.QantDomain}`,
            internalApi: 'prod-troppus-internal-api-350934821882.asia-northeast1.run.app',
          },
          tabula: {
            app: `tabula.${Const.QantDomain}`,
            api: `api.tabula.${Const.QantDomain}`,
            internalApi: `internal-api.tabula.${Const.QantDomain}`,
          },
          lp: { introduction: `intro.${Const.QantDomain}`, lp: `lp.${Const.QantDomain}` },
          neuro: {
            app: `neuro.${Const.QantDomain}`,
            api: `api.neuro.${Const.QantDomain}`,
            internalApi: `internal-api.neuro.${Const.QantDomain}`,
            voiceAgentApi: `voice-agent-api.neuro.${Const.QantDomain}`,
            agentTestApi: `agent-test-api.neuro.${Const.QantDomain}`,
          },
          pubapi: {
            api: `api.${Const.QantDomain}`,
          },
        },
        PrivateDomain: {
          neuro: { internalApi: '10.20.80.4' },
          knowledge: {
            internalApi: `internal-api.knowledge.${Const.PrivateDomain}`,
          },
          voc: {
            internalApi: `internal-api.voc.${Const.PrivateDomain}`,
          },
          tabula: {
            internalApi: '10.20.64.3',
          },
          smartFaq: {
            internalApi: `internal-api.smart-faq.${Const.PrivateDomain}`,
          },
        },
        Gcloud: Definition.Gcloud.prod,
        ActiveGcloud: Definition.Gcloud.prod.GcpProject[activeGcloudProject],
        Github: { Env: 'production' },
        Karte: {
          scriptUrl: 'https://cdn-edge.karte.io/88f40b0e31c703c5f67f1503068e14cb/edge.js',
          issuesApiHost: '10.32.8.2',
          vocApiHost: '10.32.8.2',
          speakApiHost: '10.32.8.2',
          adminDomain: 'admin.karte.io',
        },
      } satisfies SharedEnv.Var,
    }[this.Name];
    this.Var = this.BaseVar;
    this.BaseExternals = {
      POSTGRES_USER_NAME: process.env.POSTGRES_USER_NAME || '',
      SHOW_DEV_TOOLS: process.env.SHOW_DEV_TOOLS === 'true',
    };
    this.Externals = this.BaseExternals;
  }

  get DebesoInternalApiDomain(): string {
    return this.Var.Domain.debeso.internalApi;
  }

  getResourceName(system: Definition.System, name = '', env = this.Name): string {
    return [env, system, name].filter(Boolean).join('-');
  }
}
